import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { AppContext } from '../../context/Context';
import SpilleplanScreensaver from './SpilleplanScreensaver';

// Exit button, the only way out of the waiting screen (touch size)
const exitButtonStyle = {
  position: 'absolute',
  left: '50%',
  bottom: '3vh',
  transform: 'translateX(-50%)',
  zIndex: 3,
  minHeight: 90,
  padding: '0 48px',
  borderRadius: 999,
  border: 'none',
  background: 'linear-gradient(180deg, #ffd75a 0%, #ffc727 100%)',
  color: '#1a1400',
  fontSize: 26,
  fontWeight: 800,
  letterSpacing: '.02em',
  fontFamily: 'inherit',
  cursor: 'pointer',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.55)',
};

// Self-service waiting screen: today's gameplan, video fallback when there is no plan
function IdleOverlay({ idleMs = 30000, videoSrc = '', activitySignal = 0 }) {
  const [isIdle, setIsIdle] = useState(false);
  // Set by the screensaver itself when the plan comes back empty
  const [planEmpty, setPlanEmpty] = useState(false);
  const timeoutRef = useRef(null);
  const isIdleRef = useRef(false);

  const {
    updateShowModalEmptyHall,
    updateShowModalWrongKioskUser,
    updateShowModalTicketInfoKiosk,
    updateTicketInfoKiosk,

    updateShowModalPapirGameBatchResult,
    updateShowModalPapirGameNotFinished,
    updateShowModalPapirGameFinishedWithoutPrize,
    updateShowModalPapirGameFinishedWithPrize,
    updateShowModalPapirCheckCard,
  } = useContext(AppContext);

  useEffect(() => {
    isIdleRef.current = isIdle;
  }, [isIdle]);

  const resetTimer = useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(() => {
      isIdleRef.current = true;
      setIsIdle(true);

      updateShowModalEmptyHall(false);
      updateShowModalWrongKioskUser(false);
      updateShowModalTicketInfoKiosk(false);
      updateTicketInfoKiosk(false);

      updateShowModalPapirGameBatchResult(false);
      updateShowModalPapirGameNotFinished(false);
      updateShowModalPapirGameFinishedWithoutPrize(false);
      updateShowModalPapirGameFinishedWithPrize(false);
      updateShowModalPapirCheckCard(false);
    }, idleMs);
  }, [idleMs]);

  // Touches scroll the plan and never dismiss it; activity still feeds the timer
  const handleActivity = useCallback(() => {
    if (isIdleRef.current) return;

    resetTimer();
  }, [resetTimer]);

  // Wake on the exit button or an external kiosk signal (scan, card)
  const wake = useCallback(() => {
    if (isIdleRef.current) {
      isIdleRef.current = false;
      setIsIdle(false);
    }

    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    const events = ['pointerdown', 'touchstart', 'keydown', 'mousemove'];

    events.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, { passive: true });
    });

    resetTimer();

    return () => {
      events.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });

      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [handleActivity, resetTimer]);

  useEffect(() => {
    wake();
  }, [activitySignal, wake]);

  // Latched, not re-asked on every wake: a day with no games stays that way,
  // and the plan is refetched inside the screensaver anyway
  const onEmpty = useCallback(() => setPlanEmpty(true), []);

  if (!isIdle) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2000,
        backgroundColor: '#050505',
      }}
    >
      {!planEmpty && <SpilleplanScreensaver onEmpty={onEmpty} />}

      {planEmpty && videoSrc ? (
        <video
          src={videoSrc}
          autoPlay
          muted
          loop
          playsInline
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : null}

      {/* Styled here rather than in the screensaver's stylesheet: it has to sit
          over the video fallback too, and that renders without those rules */}
      <button type="button" onClick={wake} style={exitButtonStyle}>
        Tilbake til selvbetjening
      </button>
    </div>
  );
}

export default IdleOverlay;
